import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowLeft, Lightbulb, Pencil, Trash2 } from "lucide-react";
import notification from "../../../../helpers/notification";
import { TspBreadcrumb, TspConfirmDialog, TspEmptyState, TspSkeleton } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import useTspPermission from "../../hooks/useTspPermission";
import useTspFlashNotice, { navigateWithFlash } from "../../hooks/useTspFlashNotice";
import { tspErrorMessage } from "../../hooks/useTspApiResource";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import { ADVICE_SECTIONS } from "../../constants/tspAdvice";
import { formatDate } from "../../utils/tspFormat";
import { removeAdvice, useTspAdviceItem } from "./useTspAdviceData";
import { leadSection } from "./adviceModel";
import AdviceCategoryBadge from "./components/AdviceCategoryBadge";

const ADVICE_CRUMB = { label: "Advice Repository", to: "/tsp/advice" };

function AdviceDetailContent() {
  useTspFlashNotice();
  const { adviceId } = useParams();
  const navigate = useNavigate();
  const { item, related, loading, error, reload } = useTspAdviceItem(adviceId);
  const { hasPermission } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.ADVICE_MANAGE);
  const canOpenKnowledge = hasPermission(TSP_PERMISSIONS.KNOWLEDGE_REPOSITORY_VIEW);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const crumbs = (last) => [{ label: "Dashboard", to: "/tsp" }, ADVICE_CRUMB, last];

  if (loading) {
    return (
      <div className="tsp-page tsp-advice-page">
        <TspBreadcrumb items={crumbs("Advice")} />
        <TspSkeleton variant="row" count={3} label="Loading advice" />
      </div>
    );
  }

  if (error) {
    const notFound = error?.response?.status === 404;
    return (
      <div className="tsp-page tsp-advice-page">
        <TspBreadcrumb items={crumbs(notFound ? "Not found" : "Advice")} />
        {notFound ? (
          <TspEmptyState
            icon={Lightbulb}
            title="Advice not found"
            message="This advice may have been removed, or the link is incorrect."
            action={
              <Link to="/tsp/advice" className="tsp-btn tsp-btn-outline-primary">
                <ArrowLeft size={14} /> Back to Advice Repository
              </Link>
            }
          />
        ) : (
          <TspLoadError error={error} onRetry={reload} what="this advice" />
        )}
      </div>
    );
  }

  if (!item) return null;

  const cityLabel = item.location?.label || "this city";
  const editable = canManage && item.journey?.status === "IN_PROGRESS";
  const written = ADVICE_SECTIONS.filter((section) => item.sections[section.key]);
  const details = [
    { label: "Sections", value: <span className="tsp-card-action-group">{item.categories.map((key) => <AdviceCategoryBadge key={key} category={key} />)}</span> },
    {
      label: "City",
      value: canOpenKnowledge && item.location ? <Link to={`/tsp/knowledge/${item.location.id}`}>{item.location.label}</Link> : item.location?.label || "—",
    },
    { label: "Journey", value: item.journey ? <Link to={`/tsp/journeys/${item.journey.id}`}>{item.journey.name}</Link> : "Another team's journey" },
    ...(item.submitted_by ? [{ label: "Author", value: item.submitted_by.name }] : []),
    ...(item.team ? [{ label: "Team", value: item.team.name }] : []),
    { label: "Added", value: formatDate(item.created_at.slice(0, 10)) },
    ...(item.updated_at.slice(0, 10) !== item.created_at.slice(0, 10) ? [{ label: "Updated", value: formatDate(item.updated_at.slice(0, 10)) }] : []),
  ];

  const handleDeleteConfirmed = async () => {
    setDeleting(true);
    try {
      await removeAdvice(item.id);
      navigateWithFlash(navigate, "/tsp/advice", "success", "Advice deleted.");
    } catch (deleteError) {
      notification("error", tspErrorMessage(deleteError, "Could not delete this advice. Please try again."));
      setDeleting(false);
    }
  };

  return (
    <div className="tsp-page tsp-advice-page">
      <div className="tsp-journey-detail-layout">
        <div className="tsp-journey-detail-main">
          <TspBreadcrumb items={crumbs(`Advice for ${cityLabel}`)} />

          <article className="tsp-card tsp-advice-detail">
            <div className="tsp-advice-detail-head">
              <span className="tsp-card-action-group">
                {item.categories.map((key) => (
                  <AdviceCategoryBadge key={key} category={key} />
                ))}
              </span>
              <span className="tsp-muted-text">{formatDate(item.created_at.slice(0, 10))}</span>
            </div>
            <h1 className="tsp-advice-detail-title">Advice for {cityLabel}</h1>
            {item.submitted_by && (
              <p className="tsp-advice-detail-byline">
                By {item.submitted_by.name}
                {item.team ? ` · ${item.team.name}` : ""}
              </p>
            )}

            {written.map((section) => (
              <section key={section.key} aria-labelledby={`advice-${section.key}`}>
                <h4 id={`advice-${section.key}`} className="tsp-subheading">
                  {section.label}
                </h4>
                {item.sections[section.key]
                  .split(/\n\s*\n/)
                  .filter(Boolean)
                  .map((paragraph, index) => (
                    <p key={index} className="tsp-advice-detail-text">
                      {paragraph}
                    </p>
                  ))}
              </section>
            ))}
          </article>

          <section className="tsp-card" aria-labelledby="related-advice-title">
            <h3 id="related-advice-title" className="tsp-card-title">
              More advice for {cityLabel}
            </h3>
            {related.length === 0 ? (
              <p className="tsp-muted-text tsp-advice-related-empty">No other advice has been recorded for {cityLabel} yet.</p>
            ) : (
              <ul className="tsp-advice-related">
                {related.map((entry) => {
                  const lead = leadSection(entry);
                  return (
                    <li key={entry.id}>
                      {lead && <AdviceCategoryBadge category={lead.key} />}
                      <Link to={`/tsp/advice/${entry.id}`}>Advice for {entry.location?.label || cityLabel}</Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <aside className="tsp-journey-section-nav">
          <div className="tsp-card">
            <h4 className="tsp-section-nav-title">Advice Details</h4>
            <dl className="tsp-summary-list">
              {details.map((row) => (
                <div key={row.label}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="tsp-card">
            <h4 className="tsp-section-nav-title">Actions</h4>
            <div className="tsp-quick-actions-list">
              <Link to="/tsp/advice" className="tsp-btn tsp-btn-outline tsp-quick-action-full">
                <ArrowLeft size={14} /> Back to Advice
              </Link>
              {editable && (
                <>
                  <Link to={`/tsp/advice/${item.id}/edit`} className="tsp-btn tsp-btn-outline-info">
                    <Pencil size={14} /> Edit
                  </Link>
                  <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={() => setConfirmOpen(true)}>
                    <Trash2 size={14} /> Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </aside>
      </div>

      <TspConfirmDialog
        open={confirmOpen}
        title="Delete this advice?"
        message={`This advice for ${cityLabel} will be permanently deleted.`}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

// The toast container lives outside the page's loading/error/loaded branches so it is
// never remounted (which would discard a toast queued by useTspFlashNotice), and it is
// rendered first so it is subscribed before the content's effects fire.
export default function TspAdviceDetailPage() {
  return (
    <>
      <ToastContainer />
      <AdviceDetailContent />
    </>
  );
}
